import React from "react";
import {
  Trophy,
  Activity,
  Sliders,
  Users,
  BarChart3,
  Award,
} from "lucide-react";
import { ASSETS } from "./assets";

const features = [
  {
    icon: Trophy,
    title: "Americano Tournaments",
    desc: "Create tournaments for 4-32 players with automatic rotation scheduling. Every player, every round.",
    span: "lg:col-span-5",
    accent: true,
  },
  {
    icon: Activity,
    title: "Live Point-by-Point Scoring",
    desc: "Real padel scoring: 0-15-30-40 · Deuce · Advantage · Game. Tap through each point with haptic feedback.",
    span: "lg:col-span-7",
  },
  {
    icon: Sliders,
    title: "Configurable Match Formats",
    desc: "Best of 1, 3, 5, or 7 sets. Toggle Golden Point. Play it your way.",
    span: "lg:col-span-4",
  },
  {
    icon: Users,
    title: "Community Player Directory",
    desc: "Search and add registered players. Build your padel network and invite them straight into your matches.",
    span: "lg:col-span-4",
  },
  {
    icon: BarChart3,
    title: "Career Stats & Rankings",
    desc: "Track wins, points and win rate across Americano and Regular modes. Every match counts.",
    span: "lg:col-span-4",
  },
  {
    icon: Award,
    title: "Tournament Podium",
    desc: "Animated confetti celebrations with full standings and shareable results. Own the moment.",
    span: "lg:col-span-12",
    accent: true,
    wide: true,
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 md:py-24 border-t border-neutral-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3">
              Built for the court
            </div>
            <h2
              data-testid="features-heading"
              className="font-display uppercase tracking-tight text-5xl md:text-7xl leading-[0.9]"
            >
              Everything a padel
              <br />
              player needs.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md text-sm md:text-base leading-relaxed">
            Rallie packs pro-grade tournament tools and match scoring into a
            single app — designed for clubs, coaches and every weekend warrior.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`group relative border border-neutral-900 hover:border-[#D4F34A] p-6 md:p-8 transition-all duration-300 hover:-translate-y-1 ${
                  f.span
                } ${f.accent ? "bg-neutral-950" : "bg-black"}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center border ${
                      f.accent
                        ? "bg-[#D4F34A] text-black border-[#D4F34A]"
                        : "bg-transparent text-[#D4F34A] border-neutral-800"
                    }`}
                  >
                    <Icon size={20} />
                  </div>
                  <span className="font-display text-xs text-neutral-700">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight tracking-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-neutral-400 max-w-xl leading-relaxed">
                  {f.desc}
                </p>

                {f.wide && (
                  <div className="mt-8 grid md:grid-cols-2 gap-6 items-center">
                    <div className="flex flex-wrap gap-2">
                      {["1st", "2nd", "3rd", "Confetti", "Share", "Export"].map(
                        (t) => (
                          <span
                            key={t}
                            className="text-[10px] uppercase tracking-widest border border-neutral-800 px-3 py-1 rounded-full text-neutral-300"
                          >
                            {t}
                          </span>
                        ),
                      )}
                    </div>
                    <div className="relative h-52 md:h-64 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950">
                      <img
                        src={ASSETS.screenPodium}
                        alt="Rallie podium celebration"
                        className="w-full h-full object-cover"
                        style={{ transform: "scale(1.1)" }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
